/**
 * CognitoCustomerApi handles customer authentication and management
 * via AWS Cognito, including login, OTP verification, and customer creation.
 */
import { REEBELO_DOMAINS, ReebeloStoreT } from '@lambda/reebelo';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  Method,
} from '@lambda/commons/apis/axios';
import { Logger } from '@lambda/commons/logging/logger';
import SSM from '@lambda/commons/apis/ssm';
import logAxios from '@lambda/commons/logAxios';
import { CognitoCustomerRoute, OtpPurpose, SignInAuthChallenge } from './enum';
import { Customer } from '../types';
import {
  CreateCognitoCustomer,
  CognitoCustomerResponse,
  CognitoRefreshTokenResponse,
  AuthTokens,
} from './types';

export default class CognitoCustomerApi {
  private api: AxiosInstance;

  protected store: ReebeloStoreT;

  protected logger: Logger;

  private ssm: SSM;

  private url = '';

  private apiKey = '';

  private region = process.env.AWS_REGION;

  private awsClientId = '';

  private stage =
    process.env.NEXT_PUBLIC_STAGE === 'prod' || process.env.STAGE === 'prod'
      ? 'prod'
      : 'staging';

  private redirectUri = '';

  constructor(store: ReebeloStoreT) {
    this.store = store;
    this.logger = new Logger('cognito-customer-api');
    this.ssm = new SSM();
    this.api = axios.create({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    this.redirectUri =
      process.env.STAGE === 'dev'
        ? 'http://localhost:3000/login/callback'
        : `https://${REEBELO_DOMAINS[this.store]}/login/callback`;

    logAxios(this.api, this.logger);
  }

  /**
   * Initiates the forgot password process.
   */
  async forgotPassword(email: string): Promise<CognitoCustomerResponse> {
    this.logger.info(
      { email, store: this.store },
      'Initiating forgot password process',
    );

    return this.makeApiRequest(
      'POST',
      CognitoCustomerRoute.FORGOT,
      {},
      { email },
    );
  }

  /**
   * Resets the user's password using an OTP.
   */
  async resetPassword(
    email: string,
    newPassword: string,
    otp: string,
  ): Promise<CognitoCustomerResponse> {
    this.logger.info({ email, store: this.store }, 'Resetting password');

    return this.makeApiRequest(
      'POST',
      CognitoCustomerRoute.RESET,
      {},
      { email, newPassword, otp },
    );
  }

  /**
   * Logs in a customer with an optional password.
   */
  async loginCustomer(
    email: string,
    password?: string,
  ): Promise<CognitoCustomerResponse> {
    this.logger.info({ email, store: this.store }, 'Customer login attempt');
    const body: {
      email: string;
      authChallenge?: SignInAuthChallenge;
      password?: string;
    } = { email };

    if (password) {
      body.authChallenge = SignInAuthChallenge.PASSWORD;
      body.password = password;
    }

    const auth = await this.makeApiRequest<CognitoCustomerResponse>(
      'POST',
      CognitoCustomerRoute.SIGN_IN,
      {},
      body,
    );

    if (!auth) {
      this.logger.warn({ email, store: this.store }, 'Invalid login attempt');
      throw new Error('Invalid email and/or password');
    }

    return auth;
  }

  /**
   * Verifies a One-Time Password (OTP) for authentication.
   */
  async verifyOtp(
    email: string,
    otp: string,
  ): Promise<CognitoCustomerResponse> {
    this.logger.info({ email, store: this.store }, 'Verifying OTP');

    return this.makeApiRequest(
      'POST',
      CognitoCustomerRoute.OTP_VERIFY,
      {},
      { email, otp, purpose: OtpPurpose.SIGN_IN },
    );
  }

  /**
   * Resends an OTP for authentication.
   */
  async resendOtp(email: string): Promise<CognitoCustomerResponse> {
    this.logger.info({ email, store: this.store }, 'Resending OTP');

    return this.makeApiRequest(
      'POST',
      CognitoCustomerRoute.OTP_RESEND,
      {},
      { email, purpose: OtpPurpose.SIGN_IN },
    );
  }

  /**
   * Creates a new customer account.
   */
  async createCustomer(body: CreateCognitoCustomer): Promise<Customer> {
    this.logger.info(
      { store: this.store, email: body.email },
      'Creating customer',
    );

    return this.makeApiRequest('POST', CognitoCustomerRoute.SIGN_UP, {}, body);
  }

  /**
   * Retrieves the current customer based on the provided ID token.
   * If status code is between 400 and 500, we refresh the tokens and try again.
   * @param idToken The ID token to use for authentication.
   * @returns A promise that resolves to the current customer or null if the request fails.
   */
  async fetchCurrentCustomer(
    idToken: string,
    refreshToken: string,
  ): Promise<Customer | null> {
    this.logger.info(
      {
        store: this.store,
        idTokenExists: !!idToken,
        refreshTokenExists: !!refreshToken,
      },
      'Fetching current customer',
    );

    if (!idToken && !refreshToken) {
      this.logger.error(
        { store: this.store },
        'No ID token or refresh token provided',
      );

      return null;
    }
    const customer = await this.withCognitoRetry<Customer>({
      requestFn: (token) =>
        this.makeApiRequest<Customer>(
          'POST',
          CognitoCustomerRoute.CURRENT_USER,
          { Authorization: `Bearer ${token}` },
        ),
      idToken,
      refreshToken,
      refreshFn: (token) => this.refreshAWSCognitoTokens(token),
      maxRetries: 3,
    });

    this.logger.info({ customer }, 'Fetched current customer');

    return customer;
  }

  /**
   * Google Sign In Complete
   */
  async googleAuthCompleted(idToken: string): Promise<Customer | null> {
    this.logger.info({ store: this.store }, 'Creating customer');

    if (!idToken) return null;

    return this.makeApiRequest(
      'POST',
      CognitoCustomerRoute.GOOGLE_AUTH_COMPLETE,
      {
        Authorization: `Bearer ${idToken}`,
      },
    );
  }

  async cognitoExchangeCodeForToken(code: string): Promise<AuthTokens | null> {
    this.logger.info({ store: this.store }, 'Exchanging code for token');

    const url = `https://${this.store}-${this.stage}.auth.${this.region}.amazoncognito.com/oauth2/token`;

    try {
      const response = await axios.post(
        url,
        new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: await this.getAWSCognitoClientId(),
          code,
          redirect_uri: this.redirectUri,
        }).toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );

      if (response?.status === 200) return response.data;

      return null;
    } catch (err) {
      this.logger.error({ err }, 'Error exchanging code for token');
      throw new Error('Failed to exchange authorization code for token');
    }
  }

  /**
   * Generate Google Sign in Authorization URL
   */
  async googleSignInUrl(): Promise<string> {
    this.logger.info({ store: this.store }, 'Google Sign in Initiated');

    const cliendId = await this.getAWSCognitoClientId();

    if (!cliendId) return '';

    const mappedStore = this.store === 'quista' ? 'reebelo-sg' : this.store;

    return `https://${mappedStore}-${this.stage}.auth.${this.region}.amazoncognito.com/oauth2/authorize?response_type=code&client_id=${cliendId}&redirect_uri=${this.redirectUri}&identity_provider=Google&scope=openid profile email`;
  }

  /**
   * Generic method to make API requests.
   */
  protected async makeApiRequest<T>(
    method: Method,
    route: CognitoCustomerRoute,
    headers: AxiosRequestConfig['headers'] = {},
    body = {},
    query = {},
  ) {
    let url;

    try {
      url = await this.getUrl(route);
      if (!url) throw new Error('Missing API URL');

      const payload: AxiosRequestConfig = {
        method,
        url,
        headers: {
          ...headers,
          ...(await this.getHeaders()),
        },
        data: body,
        params: query,
      };

      this.logger.info({ payload }, 'Making Cognito Customer API request');

      const { data } = await axios(payload);

      this.logger.info({ data }, 'Received response from Cognito Customer API');

      return data as T;
    } catch (err: any) {
      const context = {
        store: this.store,
        method,
        route,
        url: axios.isAxiosError(err)
          ? err.config.url
          : await this.getUrl(route),
        status: axios.isAxiosError(err) ? err.response?.status : undefined,
        err: axios.isAxiosError(err) ? err.response?.data : err.message,
      };

      this.logger.error(
        { context },
        'Error making Cognito Customer API request',
      );

      throw err;
    }
  }

  /**
   * Retrieves the API URL from environment variables or SSM.
   */
  private async getUrl(path: string): Promise<string> {
    const url =
      process.env.MS_CUSTOMER_V2_URL ??
      (this.url ||
        (await this.ssm.getStoreValue(this.store, 'CUSTOMER_V2_API_URL')));

    if (!this.url && url) this.url = url;

    return this.url + path;
  }

  /**
   * Retrieves authentication headers, including API key.
   */
  private async getHeaders(): Promise<AxiosRequestConfig['headers']> {
    const apiKey =
      process.env.MS_CUSTOMER_V2_KEY ??
      (this.apiKey ||
        (await this.ssm.getStoreValue(this.store, 'CUSTOMER_V2_API_KEY')));

    if (!this.apiKey && apiKey) this.apiKey = apiKey;

    return {
      'x-api-key': apiKey,
      'x-store-name': this.store,
    };
  }

  /**
   * Generates the AWS Cognito URL. We skip MsCustomerV2 as it's not
   * necessary and will introduce latency into the flow.
   */
  private getAWSCognitoUrl(): string {
    return `https://${this.store}-${this.stage}.auth.${this.region}.amazoncognito.com/oauth2/token`;
  }

  private async getAWSCognitoClientId(): Promise<string> {
    const clientId =
      process.env.AWS_COGNITO_CLIENT_ID ??
      (await this.ssm.getStoreValue(this.store, 'AWS_COGNITO_CLIENT_ID'));

    if (!this.awsClientId && clientId) this.awsClientId = clientId;

    return this.awsClientId;
  }

  async refreshAWSCognitoTokens(
    refreshToken: string,
  ): Promise<CognitoRefreshTokenResponse | null> {
    const url = this.getAWSCognitoUrl();

    this.logger.info({ url }, 'Refreshing AWS Cognito tokens');

    if (!refreshToken) return null;

    try {
      const payload: AxiosRequestConfig = {
        method: 'POST',
        url,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: await this.getAWSCognitoClientId(),
        }).toString(),
      };

      const { data } = await axios(payload);

      this.logger.info({ data }, 'Refreshed AWS Cognito tokens');

      return data as CognitoRefreshTokenResponse;
    } catch (err: any) {
      this.logger.info({ err }, 'Plain Error refreshing AWS Cognito tokens');
      const context = {
        store: this.store,
        url: axios.isAxiosError(err) ? err.config.url : url,
        status: axios.isAxiosError(err) ? err.response?.status : undefined,
        err: axios.isAxiosError(err) ? err.response?.data : err.message,
      };

      this.logger.error({ context }, 'Error refreshing AWS Cognito tokens');

      throw err;
    }
  }

  async withCognitoRetry<T>({
    requestFn,
    idToken,
    refreshToken,
    refreshFn,
    maxRetries = 1,
  }: {
    requestFn: (idToken: string) => Promise<T>;
    idToken: string;
    refreshToken: string;
    refreshFn: (refreshToken: string) => Promise<{ id_token: string } | null>;
    maxRetries: number;
  }): Promise<T | null> {
    let currentToken = idToken;

    // eslint-disable-next-line no-plusplus
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await requestFn(currentToken);
      } catch (err: any) {
        const status = axios.isAxiosError(err)
          ? err.response?.status
          : err?.status ?? err?.code;

        const isCustomerUnauthorized = [401, 403].includes(status);

        this.logger.warn(
          { status, attempt, isCustomerUnauthorized, maxRetries },
          'Request failed; checking if retry is needed',
        );

        if (isCustomerUnauthorized && attempt <= maxRetries) {
          this.logger.info(
            { refreshToken, attempt },
            'Retrying request with refreshed tokens',
          );
          // eslint-disable-next-line no-await-in-loop
          const refreshed = await refreshFn(refreshToken);

          if (refreshed) currentToken = refreshed.id_token;
          else this.logger.error({ err, status }, 'Failed to refresh tokens');
          // eslint-disable-next-line no-continue
          continue;
        }

        this.logger.error({ err, status }, 'Request failed with no retry left');
      }
    }

    return null;
  }
}
